import { useState, useContext } from "react";
import { useNavigate } from "@tanstack/react-router";

// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";

// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
import { useBulkOrderMode } from "../../hooks/useAuthorisation";

// State Import
import { useStaffList } from "../../state/staffListState";
import { useSaveOrder } from "../../state/draftOrderSaveState";
import { getDraftDetailPath } from "../../utils/orderPaths";

// Material-ui imports
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// Component imports
import { SelectStaffDialog } from "./SelectStaffDialog";
import NewStaffDialog from "./SelectStaffNewStaffDialog";
import { SelectedStaffTable } from "./SelectedStaffTable";
import { StaffTableSearchBar } from "../../components/StaffTableSearchBar";
import { StaffTable } from "../../components/StaffTable";
import { SelectStaffOrderTotal } from "./SelectStaffOrderTotal";
// import DeclineRequestButton from "./DeclineRequestButton";

export function SelectStaff({ order }) {
  // Navigate
  const navigate = useNavigate();
  const bulkOrderMode = useBulkOrderMode();
  // Alert Context
  const { setAlertNotification } = useContext(AlertNotificationContext);

  // Order data
  const orderState = order?.data;
  const orderStaff = orderState?.staff || [];

  // Selected staff
  const [selectedRows, setSelectedRows] = useState([]);

  // Staff Filter Defaults
  const filterDefaults = {
    status: "active",
    search: "",
    branchFilter: "Mine",
    myBranchesFilter: "All",
    page: 1,
    pageSize: 25,
  };

  // Only show active staff
  const [staffFilter, setStaffFilter] = useState(filterDefaults);

  // Staff list state
  const [staffList, setStaffList] = useState({
    filter: {},
    paging: {},
    staff: [],
  });

  // Get Staff List
  const {
    data: staffData,
    isSuccess: staffSuccess,
    isPending: staffPending,
    isFetching: staffFetching,
  } = useStaffList(staffFilter);

  // Success Function
  const successFunc = () => {
    setStaffList(staffData.data.data.data.list);
  };

  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${staffData.data.status}: ${staffData.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    staffSuccess,
    staffData,
    staffData?.data?.status,
    successFunc,
    errorFunc,
  );

  // Handle selected rows
  const onRowSelectionHandler = (ids) => {
    setSelectedRows(ids);
  };

  // // Handle pre-selected rows
  // const rowSelectionModelHandler = (ids) => {
  //   setSelectedRows(ids);
  // };

  // Clear Staff Selection and reset the filter
  const clearStaffSelection = () => {
    setStaffFilter(filterDefaults);
    setSelectedRows([]);
  };

  //   Save the new order
  const {
    mutate: saveOrder,
    isSuccess: saveSuccess,
    data: saveData,
  } = useSaveOrder();

  // Handle submit staff selection dialog
  const handleStaffSelection = () => {
    const staff = [];
    if (selectedRows.length !== 0) {
      selectedRows.map((row, index) => {
        const person = { no: row, staffLineNo: index };
        staff.push(person);
      });
      saveOrder({
        data: {
          filter: orderState.filter,
          order: orderState.order,
          staff: staff,
        },
      });
    }
  };

  // Do this on save order success
  const saveSuccessFunc = () => {
    const orderNo = saveData?.data?.data?.data?.data?.order?.no;
    if (!orderNo) return;
    navigate({ to: getDraftDetailPath(orderNo, bulkOrderMode) });
  };

  // Do this on save order error
  const saveErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${saveData.data.status}: ${saveData.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    saveSuccess,
    saveData,
    saveData?.data?.status,
    saveSuccessFunc,
    saveErrorFunc,
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={2}>
        {orderStaff.length === 0 && (
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              display: "block",
            }}
          >
            Please select some staff members to start the order.
          </Typography>
        )}
        {/* If the draft order requires approval, then hide the staff selection dialog */}
        {/* {orderState.order.awaitingApproval === "No" ? (
          <></>
        ) : (
          // <DeclineRequestButton />
          <></>
        )} */}

        <SelectStaffDialog
          handleStaffSelection={handleStaffSelection}
          clearStaffSelection={clearStaffSelection}
        >
          <Box sx={{ pt: 1, height: "100%" }}>
            <StaffTableSearchBar
              myBranchesOnly
              staffFilter={staffFilter}
              setStaffFilter={setStaffFilter}
            />
          </Box>
          {staffList.staff && staffList.staff.length !== 0 ? (
            <StaffTable
              data={staffList}
              loading={staffFetching}
              selectStaff
              // onRowSelectionHandler={onRowSelectionHandler}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              selectedStaff={orderStaff}
              staffFilter={staffFilter}
              setStaffFilter={setStaffFilter}
            />
          ) : (
            !staffPending && (
              <Stack spacing={2}>
                <Typography>No staff found</Typography>
                <NewStaffDialog />
              </Stack>
            )
          )}
        </SelectStaffDialog>

        {/* Enter an optional Reference / PO number */}
        {/* {orderStaff.length !== 0 && (
          <ReferenceInput
            order={orderState.order}
            setOrder={orderState.setOrder}
          />
        )} */}

        {orderStaff.length !== 0 && (
          <SelectedStaffTable staff={orderStaff} order={orderState.order} />
        )}
        {orderStaff.length !== 0 && (
          <SelectStaffOrderTotal order={orderState.order} />
          // <Paper
          //   elevation={2}
          //   sx={{ p: 2, display: "flex", flexDirection: "row-reverse" }}
          // >
          //   <Typography sx={{ fontWeight: "bold", pl: 2 }}>
          //     Total: ...
          //   </Typography>
          // </Paper>
        )}
      </Stack>
    </Box>
  );
}
